import { Download, Zap, Brain, ArrowRight, ArrowDown } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

const steps = [
  {
    icon: Download,
    title: "Install in 60 seconds",
    description: "One-click install from Microsoft AppSource. Works in Outlook Desktop and Web.",
    number: "1",
    gradient: "from-brand-pink to-brand-orange",
  },
  {
    icon: Zap,
    title: "AI reads the signature",
    description: "The AI reads the signature and relevant email context to extract structured contact fields.",
    number: "2",
    gradient: "from-brand-orange to-brand-cyan",
  },
  {
    icon: Brain,
    title: "Review and save",
    description: "Review the draft contact, edit if needed, and click Save. The contact is written to your Outlook account.",
    number: "3",
    gradient: "from-brand-cyan to-brand-blue",
  },
];

// Desktop step component
const DesktopStep = ({ step, index }: { step: typeof steps[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-up',
    delay: index * 150,
    threshold: 0.1,
  });

  return (
    <div ref={ref} className={`relative group ${animationClass}`}>
      {/* Connecting Arrow */}
      {index < steps.length - 1 && (
        <div className="hidden lg:block absolute top-20 -right-6 w-12 h-0.5 bg-gradient-to-r from-primary/30 to-transparent z-0">
          <ArrowRight className="absolute -right-1 -top-2 w-5 h-5 text-primary/30" />
        </div>
      )}

      {/* Card */}
      <div className="relative glass-card-strong rounded-2xl p-6 lg:p-8 h-full border border-border/50 hover-lift hover-glow">
        {/* Number Badge */}
        <div className={`absolute -top-5 -left-5 lg:-top-6 lg:-left-6 w-12 h-12 lg:w-16 lg:h-16 bg-gradient-to-br ${step.gradient} rounded-xl lg:rounded-2xl flex items-center justify-center shadow-elevated rotate-3 group-hover:rotate-6 transition-transform duration-300`}>
          <span className="font-bold text-xl lg:text-2xl text-white">{index + 1}</span>
        </div>
        
        {/* Icon */}
        <div className="mb-6 lg:mb-8 inline-flex p-4 lg:p-5 rounded-xl lg:rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5">
          <step.icon className="w-8 h-8 lg:w-10 lg:h-10 text-primary" />
        </div>
        
        <h3 className="text-xl lg:text-2xl font-bold mb-3 lg:mb-4 group-hover:text-primary transition-colors">
          {step.title}
        </h3>
        
        <p className="text-muted-foreground leading-relaxed text-base lg:text-lg">
          {step.description}
        </p>

        {/* Decorative Corner Accent */}
        <div className={`absolute bottom-4 right-4 w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br ${step.gradient} opacity-5 rounded-tl-[3rem] group-hover:opacity-10 transition-opacity`} />
      </div>
    </div>
  );
};

// Mobile step component
const MobileStep = ({ step, index }: { step: typeof steps[0]; index: number }) => {
  const { ref, animationClass } = useScrollAnimation<HTMLDivElement>({
    type: 'slide-right',
    delay: index * 150,
    threshold: 0.1,
  });

  return (
    <div ref={ref} className={`relative ${animationClass}`}>
      {/* Vertical Connector */}
      {index < steps.length - 1 && (
        <div className="absolute left-6 top-full w-0.5 h-6 bg-gradient-to-b from-primary/30 to-transparent z-0">
          <ArrowDown className="absolute -bottom-3 -left-2 w-5 h-5 text-primary/30" />
        </div>
      )}

      {/* Card */}
      <div className="relative glass-card-strong rounded-xl p-5 border border-border/50 touch-active">
        {/* Number Badge */}
        <div className={`absolute -top-3 -left-3 w-10 h-10 bg-gradient-to-br ${step.gradient} rounded-lg flex items-center justify-center shadow-elevated`}>
          <span className="font-bold text-lg text-white">{index + 1}</span>
        </div>
        
        <div className="ml-6">
          {/* Icon */}
          <div className="mb-4 inline-flex p-3 rounded-lg bg-gradient-to-br from-primary/10 to-primary/5">
            <step.icon className="w-6 h-6 text-primary" />
          </div>
          
          <h3 className="text-lg font-bold mb-2">
            {step.title}
          </h3>
          
          <p className="text-sm text-muted-foreground leading-relaxed">
            {step.description}
          </p>
        </div>
      </div>
    </div>
  );
};

export const HowItWorks = () => {
  return (
    <section id="how-it-works" className="py-20 md:py-32 bg-gradient-feature relative overflow-hidden">
      {/* Animated Background Elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 right-20 w-48 md:w-72 h-48 md:h-72 bg-brand-pink/10 rounded-full blur-[100px] animate-blob" />
        <div className="absolute bottom-20 left-20 w-48 md:w-72 h-48 md:h-72 bg-brand-cyan/10 rounded-full blur-[100px] animate-blob animation-delay-2000" />
      </div>

      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(0,0,0,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(0,0,0,0.02)_1px,transparent_1px)] bg-[size:3rem_3rem] dark:bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)]" />

      <div className="container px-4 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-12 md:mb-20">
          <div className="inline-block px-4 py-2 mb-4 md:mb-6 glass-card rounded-full">
            <span className="text-sm font-semibold bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent">
              How It Works
            </span>
          </div>

          <h2 className="text-3xl sm:text-4xl md:text-6xl font-bold mb-4 md:mb-6">
            Three steps.{" "}
            <span className="bg-gradient-to-r from-brand-pink via-brand-orange to-brand-cyan bg-clip-text text-transparent animate-gradient-shift">
              Zero complexity.
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-muted-foreground">
            Start with 20 free credits. Upgrade only when you need more.
          </p>
        </div>
        
        {/* Desktop: Horizontal Grid */}
        <div className="hidden md:grid md:grid-cols-3 gap-8 lg:gap-12 max-w-6xl mx-auto">
          {steps.map((step, index) => (
            <DesktopStep key={index} step={step} index={index} />
          ))}
        </div>

        {/* Mobile: Vertical Timeline */}
        <div className="md:hidden space-y-6">
          {steps.map((step, index) => (
            <MobileStep key={index} step={step} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};
